import { DateTime } from "luxon";
/**
 * Danish two-letter weekday abbreviations, indexed by Luxon's `weekday` (1 = Monday).
 *
 * moment's `dd` token renders exactly these, and Luxon has no equivalent: `ccccc` is one letter
 * ("M", "T") and `ccc` is "man.", "tors.". Slicing `ccc` to two characters happens to produce the
 * right answer today, but it depends on the ICU short-name spelling, so the mapping is written out
 * instead of derived.
 */
const DANISH_MIN_WEEKDAY = ["ma", "ti", "on", "to", "fr", "lø", "sø"];
// Written out for the same reason: the list rows of the redesign read "lør. 17. okt · 18:30–21:45".
const DANISH_SHORT_WEEKDAY = ["man.", "tir.", "ons.", "tor.", "fre.", "lør.", "søn."];
const DANISH_SHORT_MONTH = ["jan.", "feb.", "mar.", "apr.", "maj", "jun.", "jul.", "aug.", "sep.", "okt.", "nov.", "dec."];
const DANISH_WEEKDAY = ["mandag", "tirsdag", "onsdag", "torsdag", "fredag", "lørdag", "søndag"];
const DANISH_MONTH = ["Januar", "Februar", "Marts", "April", "Maj", "Juni", "Juli", "August", "September", "Oktober", "November", "December"];
class DateUtility {
}
/**
 * Parses a date-time the BioPlanner API returned, in either of the two shapes it emits.
 *
 * Anything typed as `DateTime` on the server is serialized by System.Text.Json as real ISO
 * 8601 ("2026-09-01T14:45:00"). But the models carrying their times as **strings** -
 * `ShiftModel.StartTime`/`EndTime` among them, which is what the week schedule returns - are
 * built by `DbConvert.ToIso`, and despite the name that emits `"yyyy-MM-dd HH:mm:00"`: a
 * space separator, the SQL shape.
 *
 * moment's lenient parser swallowed both, which is why this never needed saying before.
 * Luxon's `fromISO` correctly rejects the second and returns an invalid DateTime, so a
 * straight port would have rendered "Invalid DateTime" across the whole schedule. `fromSQL`
 * is the matching strict parser, so this tries ISO first and falls back to it rather than
 * rewriting the string or reaching for a lenient parse.
 */
DateUtility.parse = (value) => {
    const iso = DateTime.fromISO(value);
    return iso.isValid ? iso : DateTime.fromSQL(value);
};
/**
 * Groups every day from `fromDate` to `toDate` inclusive into ISO weeks, building each day's
 * item through `createItem`.
 *
 * The v13 original leaned on moment's mutability - `day.add(1, "days")` moved a single shared
 * instance, which every `createItem` call had already been handed. Luxon's DateTime is
 * immutable, so the loop reassigns and each call gets its own instance.
 */
DateUtility.weekify = (fromDate, toDate, createItem) => {
    const weeks = [];
    let day = fromDate.startOf("day");
    const endDt = toDate.endOf("day");
    do {
        const weekNo = day.toFormat("WW");
        let week = weeks.find((w) => w.weekNumber === weekNo);
        if (!week) {
            week = { weekNumber: weekNo, days: [] };
            weeks.push(week);
        }
        week.days.push(createItem(day, week));
        day = day.plus({ days: 1 });
    } while (day.valueOf() < endDt.valueOf());
    return weeks;
};
DateUtility.absoluteMinutesFromMidnight = (dt) => dt.hour * 60 + dt.minute;
/**
 * The one-line label a shift is listed under, e.g. `[36] fr 04.09.26 17:00-22:00`.
 *
 * The time is `toFormat("HH:mm")`, deliberately not `toLocaleString(TIME_SIMPLE)`: under the
 * `da` locale Luxon renders the locale-aware form with a **dot** separator ("17.00"), where
 * moment's `LT` gave a colon. The colon is what this site has always shown.
 */
DateUtility.stringify = (dtStart, dtEnd) => {
    const week = dtStart.toFormat("WW");
    const day = DANISH_MIN_WEEKDAY[dtStart.weekday - 1];
    const date = dtStart.toFormat("dd.MM.yy");
    const time = `${dtStart.toFormat("HH:mm")}-${dtEnd.toFormat("HH:mm")}`;
    return `[${week}] ${day} ${date} ${time}`;
};
/** The day of a shift in the list rows, e.g. `lør. 17. okt`. */
DateUtility.rowDay = (dt) => `${DANISH_SHORT_WEEKDAY[dt.weekday - 1]} ${dt.day}. ${DANISH_SHORT_MONTH[dt.month - 1]}`;
/** The interval of a shift in the list rows, e.g. `18:30–21:45`. */
DateUtility.rowInterval = (dtStart, dtEnd) => `${dtStart.toFormat("HH:mm")}–${dtEnd.toFormat("HH:mm")}`;
/** The label of a month bar: `Oktober`, and `Januar 2027` when it is not this year. */
DateUtility.monthLabel = (dt) => DANISH_MONTH[dt.month - 1] + (dt.year === DateTime.now().year ? "" : ` ${dt.year}`);
/** The weekday as the tile of the next shift shows it, e.g. `lør`. */
DateUtility.weekdayShort = (dt) => DANISH_SHORT_WEEKDAY[dt.weekday - 1].replace(".", "");
/** The month as the tile of the next shift shows it, e.g. `okt`. */
DateUtility.monthShort = (dt) => DANISH_SHORT_MONTH[dt.month - 1].replace(".", "");
/** The month as the Top 5 shows it, e.g. `september 2026`. */
DateUtility.monthYear = (dt) => `${DANISH_MONTH[dt.month - 1].toLowerCase()} ${dt.year}`;
/**
 * When a shift is, as a phrase to put after "Din næste vagt er": `i aften`, `i morgen aften`,
 * or the date, e.g. `tirsdag d. 20. oktober`, from the day after tomorrow on.
 */
DateUtility.whenPhrase = (dtStart, now = DateTime.now()) => {
    const days = Math.round(dtStart.startOf("day").diff(now.startOf("day"), "days").days);
    const part = dtStart.hour < 12 ? "formiddag" : dtStart.hour < 17 ? "eftermiddag" : "aften";
    if (days <= 0) {
        return part === "aften" ? "i aften" : `i dag ${part}`;
    }
    if (days === 1) {
        return `i morgen ${part}`;
    }
    return `${DANISH_WEEKDAY[dtStart.weekday - 1]} d. ${dtStart.day}. ${DANISH_MONTH[dtStart.month - 1].toLowerCase()}`;
};
/**
 * The days of a week as the head of the Vagtplan says it: `12.–18. oktober`, and
 * `28. september–4. oktober` when the week turns into another month.
 */
DateUtility.weekRange = (monday, withYear = false) => {
    const sunday = monday.plus({ days: 6 });
    const month = (dt) => DANISH_MONTH[dt.month - 1].toLowerCase();
    const year = withYear ? ` ${sunday.year}` : "";
    return monday.month === sunday.month
        ? `${monday.day}.–${sunday.day}. ${month(sunday)}${year}`
        : `${monday.day}. ${month(monday)}–${sunday.day}. ${month(sunday)}${year}`;
};
/** The weekday as the day strip and the week grid say it: `MAN`. */
DateUtility.weekdayCaps = (dt) => DateUtility.weekdayShort(dt).toUpperCase();
/** The day a group of messages in the inbox is under: `I dag`, `I går`, then `Tirsdag 13. oktober`. */
DateUtility.messageDay = (dt, now = DateTime.now()) => {
    const days = Math.round(now.startOf("day").diff(dt.startOf("day"), "days").days);
    if (days === 0)
        return "I dag";
    if (days === 1)
        return "I går";
    return DateUtility.longDay(dt) + (dt.year === now.year ? "" : ` ${dt.year}`);
};
/** The day of a shift as the head of Vagtinfo says it: `Lørdag 10. oktober`. */
DateUtility.longDay = (dt) => {
    const weekday = DANISH_WEEKDAY[dt.weekday - 1];
    return `${weekday.charAt(0).toUpperCase()}${weekday.slice(1)} ${dt.day}. ${DANISH_MONTH[dt.month - 1].toLowerCase()}`;
};
export default DateUtility;
